import { useEffect, useRef } from "react";
import type { CatalogSelectFilter } from "@apex/shared";
import { cn } from "@apex/shared";

export interface SelectFilterPopoverProps {
  filter: CatalogSelectFilter;
  /** Currently selected option value (single-select for v1). */
  value: string;
  /** Called when the user selects an option. */
  onSelect: (value: string) => void;
  /** Called when the popover should close. */
  onClose: () => void;
  className?: string;
}

/**
 * SelectFilterPopover — popover with a checkbox list of options. The list
 * supports single-select for v1 (multi-select / type-ahead are out of scope
 * per the Section 2 spec). Selecting an option commits immediately and
 * closes the popover; selecting "All" / clicking the active option clears
 * the filter.
 */
export function SelectFilterPopover({
  filter,
  value,
  onSelect,
  onClose,
  className,
}: SelectFilterPopoverProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocumentMouseDown(e: MouseEvent) {
      const node = containerRef.current;
      if (!node) return;
      // Use composedPath() rather than `node.contains(e.target)` so the guard
      // survives Shadow DOM event retargeting (B2). When the widget runs
      // inside `<apex-widget>` the browser rewrites `e.target` to the host
      // element by the time the event hits `document`, so a plain
      // `contains()` check would treat a click on the checkbox as "outside",
      // close the popover before the change event fires, and silently
      // discard the selection.
      const path = typeof e.composedPath === "function" ? e.composedPath() : [];
      if (path.includes(node)) return;
      // Fallback for environments where composedPath() is unavailable.
      if (node.contains(e.target as Node)) return;
      onClose();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("mousedown", onDocumentMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  function handleToggle(optionValue: string) {
    // Single-select: clicking the active option clears it.
    onSelect(value === optionValue ? "" : optionValue);
    onClose();
  }

  return (
    <div
      ref={containerRef}
      data-testid="apex-select-popover"
      role="dialog"
      aria-label={filter.displayName}
      className={cn(
        "apex-absolute apex-top-full apex-left-0 apex-mt-2 apex-z-30",
        "apex-w-[16rem] apex-max-h-[20rem] apex-overflow-auto",
        "apex-rounded-md apex-border apex-border-border apex-bg-background apex-shadow-lg",
        "apex-p-2",
        className
      )}
    >
      <div className="apex-flex apex-items-center apex-justify-between apex-px-2 apex-pt-1 apex-pb-2 apex-border-b apex-border-border apex-mb-1">
        <p className="apex-text-sm apex-font-semibold apex-text-foreground">
          {filter.displayName}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="apex-rounded apex-p-1 hover:apex-bg-muted apex-text-muted-foreground"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
      <ul className="apex-flex apex-flex-col">
        {filter.options.map((option) => {
          const checked = value === option.value;
          return (
            <li key={option.id}>
              <label
                className={cn(
                  "apex-flex apex-items-center apex-gap-2 apex-px-2 apex-py-1.5 apex-rounded apex-cursor-pointer",
                  "hover:apex-bg-muted apex-text-sm",
                  checked && "apex-bg-primary/5 apex-text-primary"
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleToggle(option.value)}
                  className="apex-h-4 apex-w-4 apex-rounded apex-border-input apex-cursor-pointer"
                />
                <span>{option.displayName}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
